import React from 'react';
import { gql } from '@soundxyz/gql-string';
import { styled } from '@components-shared/src/stitches';
import { paintCanvas } from '@components/Waveform/paintCanvas';
import waveformAvgChunker from '@components/Waveform/waveformAvgChunker';
import {
  getFragment,
  useQuery,
  WaveformExpandedPlayerDocument,
  WaveformExpandedPlayerInfoFragmentDoc,
} from '@graphql/reactQuery';
import { useSafeLayoutEffect } from '@hooks/useSafeLayoutEffect';
import { useAudioController } from '@store/audio/AudioController';
import { useAudioPosition } from '@store/audio/AudioPosition';
import { WaveformComments } from './WaveformComments';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment WaveformExpandedPlayerInfo on Release {
    id
    track {
      id
      normalizedPeaks
    }

    ...WaveformCommentsReleaseInfo
  }
  query WaveformExpandedPlayer($releaseId: UUID!) {
    release(id: $releaseId) {
      id
      ...WaveformExpandedPlayerInfo
    }
  }
`);

const RESOLUTION_MULTIPLIER = 4;

const BAR_WIDTH = 2;
const BAR_MARGIN = 2;
const HEIGHT = 68;
const PLAYING_COLOR = 'hsla(227, 0%, 100%, 0.8)';
const UNPLAYED_COLOR = 'hsla(227, 0%, 100%, 0.14)';

export function Waveform({ releaseId, isOpen }: { releaseId: string; isOpen: boolean }) {
  const { activeTrackId } = useAudioController();
  const [waveformWidth, setWaveformWidth] = React.useState(0);
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const waveformData = useQuery(WaveformExpandedPlayerDocument, {
    variables: {
      releaseId,
    },
    staleTime: 0,
    enabled: isOpen,
  });

  const release = getFragment(
    WaveformExpandedPlayerInfoFragmentDoc,
    waveformData?.data?.data?.release,
  );
  const track = release?.track;

  const isDisabled = !release;

  const { percentComplete } = useAudioPosition();

  const isActiveTrack = track?.id === activeTrackId;

  const trackPerc = isDisabled ? 0 : isActiveTrack ? percentComplete / 100 : 0;

  /**
   * We offset to make the waveform be insync with the audio slider
   */
  const playingOffset = React.useMemo(() => {
    if (percentComplete < 0.2) {
      return 0;
    }
    if (percentComplete > 0.2 && percentComplete < 1.5) {
      return 0.5;
    }
    if (percentComplete > 1.5 && percentComplete < 50) {
      return 1;
    }

    return 0.5;
  }, [percentComplete]);

  const playingBarNum = Math.floor(
    (trackPerc * waveformWidth) / (BAR_WIDTH + BAR_MARGIN) + playingOffset,
  );

  const chunkedPeaks = React.useMemo(() => {
    const normalizedPeaks = track?.normalizedPeaks || [];

    const numBars = Math.floor(waveformWidth / (BAR_WIDTH + BAR_MARGIN));
    return waveformAvgChunker(normalizedPeaks, numBars);
  }, [track?.normalizedPeaks, waveformWidth]);

  const paintWaveformCanvas = React.useCallback(() => {
    if (!canvasRef.current) {
      return;
    }
    paintCanvas({
      canvasRef,
      normalizedPeaks: chunkedPeaks,
      maxBarHeight: HEIGHT * RESOLUTION_MULTIPLIER,
      barWidth: BAR_WIDTH * RESOLUTION_MULTIPLIER,
      barMargin: BAR_MARGIN * RESOLUTION_MULTIPLIER,
      playingBarNum,
      hoverXCoord: 0,
      isActiveTrack,
      unPlayedColor: UNPLAYED_COLOR,
      playedColor: PLAYING_COLOR,
      showBottomHalf: false,
    });
  }, [chunkedPeaks, isActiveTrack, playingBarNum]);

  React.useEffect(() => {
    paintWaveformCanvas();
  }, [paintWaveformCanvas]);

  useSafeLayoutEffect(() => {
    if (containerRef.current) {
      setWaveformWidth(containerRef.current.offsetWidth);
    }
  }, []);

  return (
    <Container ref={containerRef}>
      <canvas
        style={{
          height: HEIGHT,
          display: 'block',
          cursor: isDisabled ? 'default' : 'pointer',
          WebkitTapHighlightColor: 'transparent',
          width: '100%',
        }}
        ref={canvasRef}
        height={HEIGHT * RESOLUTION_MULTIPLIER}
        width={waveformWidth * RESOLUTION_MULTIPLIER}
      />
      {/* waveform bottom is not as smooth so we cut it off and hide it with this line (: */}
      <Line />
      {!!release && (
        <WaveformContainer>
          <WaveformComments release={release} width={waveformWidth} />
        </WaveformContainer>
      )}
    </Container>
  );
}

const Container = styled('div', {
  display: 'flex',
  position: 'relative',
  justifyContent: 'center',
  alignItems: 'center',
  margin: '-16px auto 0',
  width: '99%',
  background: 'transparent',
  bottom: -32,
});

const WaveformContainer = styled('div', {
  position: 'absolute',
  left: 0,
  top: 38,
  zIndex: '$above2',
});

const Line = styled('div', {
  position: 'absolute',
  bottom: 23,
  width: '100%',
  height: 5,
  backgroundColor: '#000',
  zIndex: '$above1',
});
